'use client'

import { type AxiosError, type AxiosResponse } from 'axios'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'

import { type LoginOtpConfirmRequestV3 } from '@redteclab/api/clients/bully'

import { buildHomeOneApiAxiosInstance } from '../../../base-api-communication'
import { type BackendApiResponseError } from '../../../base-errors'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../global-config'
import { urlResolverGetOneTimePasswordConfirmApiEndpoint } from '../../../url-handling'

import { refetchSwrsWhenUserLogsIn } from './useUserApiLoginMutation'

const postOneTimePasswordLoginData =
  (globalConfig: PublicGlobalConfig) =>
  async (
    swrKey: string,
    data: { arg: LoginOtpConfirmRequestV3 },
  ): Promise<AxiosResponse> => {
    return buildHomeOneApiAxiosInstance(globalConfig).post(
      urlResolverGetOneTimePasswordConfirmApiEndpoint(globalConfig),
      data.arg,
      {
        headers: {
          ...(process.env.NEXT_PUBLIC_SKIP_FRIENDLY_CAPTCHA === 'true' && {
            'skip-friendly-captcha-verification': 'true',
          }),
        },
      },
    )
  }

const getOneTimePasswordApiLoginMutationKey = (): string => {
  return 'useOneTimePasswordApiLoginMutation'
}

export const useOneTimePasswordApiLoginMutation = ({
  onError,
  onSuccess,
}: {
  onError: (err: AxiosError<BackendApiResponseError>) => void
  onSuccess?: (
    afterLoginSideEffectsPromise: Promise<string | undefined>,
  ) => void
}): SWRMutationResponse<
  AxiosResponse,
  AxiosError<BackendApiResponseError>,
  string,
  LoginOtpConfirmRequestV3
> => {
  const globalConfig = useGlobalConfigContext()

  return useSWRMutation(
    getOneTimePasswordApiLoginMutationKey(),
    postOneTimePasswordLoginData(globalConfig),
    {
      onError,
      onSuccess: () => {
        onSuccess?.(refetchSwrsWhenUserLogsIn())
      },
    },
  )
}
